import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Loader2, Receipt, ShieldCheck, ShoppingBag, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useCart } from '../contexts/CartContext';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getApiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((entry) => entry?.msg || entry?.message)
      .filter(Boolean);
    if (messages.length) return messages.join(', ');
  }
  if (detail && typeof detail === 'object') {
    return detail.message || detail.description || JSON.stringify(detail);
  }
  return error?.message || fallback;
}

const statusColor = {
  active: 'bg-slate-500',
  processing_payment: 'bg-amber-500 text-black',
  pending_payment: 'bg-amber-500 text-black',
  pending_counter: 'bg-cyan-500 text-black',
  paid: 'bg-blue-500',
  in_queue: 'bg-purple-500',
  order_placed: 'bg-green-700',
  cancelled: 'bg-slate-500',
};


const CartLine = React.memo(function CartLine({ item, disabled, mutating, onDecrease, onIncrease }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:flex-row sm:items-center sm:justify-between sm:p-4">
      <div className="flex min-w-0 items-center gap-3">
        <img src={item.image_url || '/logo.png'} alt={item.title || item.name} className="h-16 w-16 shrink-0 rounded-xl border object-cover" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{item.title || item.name}</p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">₹{item.price} x {item.quantity}</p>
          {((item.actual_price ?? item.original_price ?? 0) > item.price) && <p className="text-xs text-slate-400 line-through">₹{item.actual_price ?? item.original_price}</p>}
        </div>
      </div>
      <div className="ml-auto flex items-center gap-2 rounded-full border bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-800">
        <Button variant="ghost" size="sm" className="h-8 w-8 rounded-full p-0 text-base" disabled={disabled} onClick={onDecrease}>-</Button>
        <span className="w-6 text-center text-sm font-semibold">{mutating ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : item.quantity}</span>
        <Button variant="ghost" size="sm" className="h-8 w-8 rounded-full p-0 text-base" disabled={disabled} onClick={onIncrease}>+</Button>
      </div>
    </div>
  );
});

export default function UserCart() {
  const { user, loading } = useAuth();
  const { socket } = useSocket();
  const { activeCart, refreshCarts, updateItemQuantity, resetCartLifecycle, setCarts, itemMutating, hydrated, flushPendingAdds } = useCart();
  const [cartPreparing, setCartPreparing] = useState(true);
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState('');
  const [giftCardCode, setGiftCardCode] = useState('');
  const [showPromo, setShowPromo] = useState(false);
  const [showGift, setShowGift] = useState(false);
  const [pending, setPending] = useState(false);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [paymentStage, setPaymentStage] = useState('idle');
  const [paymentOrderId, setPaymentOrderId] = useState(null);
  const [shopOpen, setShopOpen] = useState(true);
  const cart = activeCart || null;
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const fetchShopStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/shop-status`);
      setShopOpen(data?.shop_open !== false);
    } catch (error) {
      setShopOpen(true);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/user-login');
      return;
    }
    let mounted = true;
    const prepareCart = async () => {
      setCartPreparing(true);
      try {
        fetchShopStatus();
        await flushPendingAdds();
        await refreshCarts({ silent: true });
      } finally {
        if (mounted) setCartPreparing(false);
      }
    };
    prepareCart();
    return () => { mounted = false; };
  }, [loading, user, navigate, fetchShopStatus, refreshCarts, flushPendingAdds]);

  useEffect(() => {
    if (!socket) return;
    const refresh = () => refreshCarts({ silent: true });
    socket.on('cart_updated', refresh);
    socket.on('order_updated', refresh);
    return () => {
      socket.off('cart_updated', refresh);
      socket.off('order_updated', refresh);
    };
  }, [socket, refreshCarts]);


  const locked = cart?.is_locked;
  const pendingCounterPayment = cart?.cart_status === 'pending_counter';

  const priceSummary = useMemo(() => {
    const lines = cart?.items || [];
    const actualPrice = Number(cart?.total_actual_price ?? cart?.actual_total ?? lines.reduce((sum, item) => sum + Number(item.actual_price ?? item.original_price ?? item.price ?? 0) * Number(item.quantity || 0), 0));
    const currentPrice = Number(cart?.total_current_price ?? cart?.current_total ?? lines.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0));
    const menuDiscount = Math.max(0, Number(cart?.total_discount ?? actualPrice - currentPrice));
    const promoDiscount = Number(cart?.discount_amount || 0);
    const giftDiscount = Number(cart?.gift_card_amount || 0);
    const total = Number(cart?.total || 0);
    const payableTotal = Math.round(total);
    const totalSaved = Math.max(0, menuDiscount + promoDiscount + giftDiscount);
    return { actualPrice, currentPrice, menuDiscount, promoDiscount, giftDiscount, totalSaved, total, payableTotal };
  }, [cart]);

  const showCartSkeleton = !hydrated || loading || cartPreparing;


  const updateQty = useCallback(async (itemId, delta) => {
    if (!shopOpen || !cart || locked || pendingCounterPayment || pending || itemMutating[itemId]) return;
    try {
      await updateItemQuantity(cart.id, itemId, delta);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to update quantity');
    }
  }, [shopOpen, cart, locked, pendingCounterPayment, pending, itemMutating, updateItemQuantity]);

  const applyCode = async (kind) => {
    if (!shopOpen || !cart || pending) return;
    try {
      if (kind === 'promo') {
        const normalizedCode = promoCode.trim().toUpperCase();
        if (normalizedCode && normalizedCode === (cart?.promo_code || '').toUpperCase()) {
          toast.info('Promo code already applied');
          return;
        }
        const { data } = await axios.post(`${API}/carts/${cart.id}/apply-promo-code`, { code: promoCode });
        setCarts((prev) => prev.map((entry) => (entry.id === data.id ? data : entry)));
        toast.success('Promo code applied successfully');
      } else {
        const normalizedCode = giftCardCode.trim().toUpperCase();
        if (normalizedCode && normalizedCode === (cart?.gift_card_code || '').toUpperCase()) {
          toast.info('Gift card already applied');
          return;
        }
        const { data } = await axios.put(`${API}/carts/${cart.id}`, { gift_card_code: giftCardCode });
        setCarts((prev) => prev.map((entry) => (entry.id === data.id ? data : entry)));
        if (data?.gift_card_amount > 0) {
          toast.success('Gift card applied successfully');
        } else {
          toast.error('Invalid gift card');
        }
      }
    } catch (error) {
      if (kind === 'promo') {
        toast.error(error?.response?.data?.detail || 'Invalid promo code');
      } else {
        toast.error(error?.response?.data?.detail || 'Invalid gift card');
      }
    }
  };

  const removeAppliedCode = async (kind) => {
    if (!shopOpen || !cart || pending) return;
    try {
      const payload = kind === 'promo' ? { promo_code: '' } : { gift_card_code: '' };
      const { data } = await axios.put(`${API}/carts/${cart.id}`, payload);
      setCarts((prev) => prev.map((entry) => (entry.id === data.id ? data : entry)));
      toast.success(kind === 'promo' ? 'Promo code removed' : 'Gift card removed');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to remove code');
    }
  };

  const handleCounterQR = async () => {
    if (!shopOpen || !cart || pending) return;
    setPending(true);
    try {
      const { data: confirmed } = await axios.post(`${API}/carts/confirm`, { cart_id: cart.id });
      await axios.post(`${API}/carts/${cart.id}/generate-qr`);
      setCarts((prev) => prev.map((entry) => (entry.id === confirmed.id ? confirmed : entry)));
      toast.success('Counter QR generated');
      refreshCarts({ silent: true });
    } catch (error) {
      console.error('Could not generate counter QR:', error);
      toast.error(getApiErrorMessage(error, 'Could not generate counter QR'));
    } finally {
      setPending(false);
    }
  };

  const handleOnlinePayment = async () => {
    if (!shopOpen || !cart || pending || paymentInProgress) return;
    setPending(true);
    try {
      const cartId = cart?._id || cart?.id;
      if (!cartId) {
        throw new Error('Cart id is missing');
      }
      await axios.post(`${API}/carts/confirm`, { cart_id: cartId });
      const order = await axios.post(`${API}/payments/create-order`, { cart_id: cartId });
      console.log('ORDER:', order.data);
      const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;
      if (!razorpayKey) {
        throw new Error('REACT_APP_RAZORPAY_KEY_ID is missing');
      }
      const options = {
        key: razorpayKey,
        amount: order.data.amount,
        currency: order.data.currency,
        order_id: order.data.id,
        name: 'K-Crunch',
        modal: {
          ondismiss: async () => {
            try {
              await axios.post(`${API}/payments/mark-failed`, {
                cart_id: cartId,
                razorpay_order_id: order.data.id,
                reason: 'checkout_dismissed',
              });
            } catch (dismissError) {
              console.warn('Failed to reset cart after dismiss', dismissError);
            }
            setPaymentInProgress(false);
            setPending(false);
                  refreshCarts({ silent: true });
          },
        },
        handler: async (response) => {
          console.log('PAYMENT RESPONSE:', response);
          setPaymentInProgress(false);
          setPaymentStage('confirming');
          try {
            const verifyPromise = axios.post(`${API}/payments/verify`, {
              cart_id: cartId,
              razorpay_order_id: order.data.id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            await delay(650);
            setPaymentStage('confirmed');
            const verifyResponse = await verifyPromise;
            resetCartLifecycle();
            setPromoCode('');
            setGiftCardCode('');
            setShowPromo(false);
            setShowGift(false);
            setPaymentOrderId(verifyResponse?.data?.order?.id || null);
            const { data: latestOrders } = await axios.get(`${API}/orders/my`);
            sessionStorage.setItem('prefetched_orders', JSON.stringify(latestOrders || []));
            toast.success('Order placed successfully', { duration: 1000 });
            await delay(1000);
            setPaymentOrderId(null);
            setPaymentStage('idle');
            setPending(false);
            navigate('/orders');
          } catch (verifyError) {
            toast.error(verifyError?.response?.data?.detail || 'Payment verification failed');
            try {
              await axios.post(`${API}/payments/mark-failed`, {
                cart_id: cartId,
                razorpay_order_id: response?.razorpay_order_id || order.data.id,
                reason: 'verify_failed',
              });
            } catch (markFailedError) {
              console.warn('Failed to reset cart after verification failure', markFailedError);
            }
            setPaymentOrderId(null);
            setPaymentStage('idle');
            setPending(false);
                  refreshCarts({ silent: true });
          }
        },
      };
      setPaymentInProgress(true);
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', async (response) => {
        toast.error(response?.error?.description || 'Payment failed');
        try {
          await axios.post(`${API}/payments/mark-failed`, {
            cart_id: cartId,
            razorpay_order_id: order.data.id,
            reason: response?.error?.description || 'payment_failed',
          });
        } catch (markFailedError) {
          console.warn('Failed to reset cart after payment failure', markFailedError);
        }
        setPaymentInProgress(false);
        setPaymentOrderId(null);
        setPaymentStage('idle');
        setPending(false);
          refreshCarts({ silent: true });
      });
      rzp.open();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Online payment failed');
      setPaymentInProgress(false);
      setPaymentOrderId(null);
      setPaymentStage('idle');
      setPending(false);
    } finally {
      // Keep pending true while Razorpay modal is open; it is reset on dismiss/verify/catch.
    }
  };

  const handleCancelCounterCart = async () => {
    if (!cart || pending || !pendingCounterPayment) return;
    setPending(true);
    try {
      await axios.delete(`${API}/carts/${cart.id}`);
      resetCartLifecycle();
      toast.success('Counter payment cart cancelled');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not cancel cart');
      refreshCarts({ silent: true });
    } finally {
      setPending(false);
    }
  };

  if (showCartSkeleton) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#020617]">
        <div className="mx-auto max-w-5xl px-4 py-6 space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full rounded-2xl" />
          <Skeleton className="h-24 w-full rounded-2xl" />
          <Skeleton className="h-24 w-full rounded-2xl" />
          <Skeleton className="h-40 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 dark:bg-slate-950 md:px-8">
      <AnimatePresence>
        {paymentStage !== 'idle' && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.22 }}
              className="w-[90%] max-w-md rounded-2xl border border-white/15 bg-slate-900/95 p-8 text-center shadow-2xl"
            >
              <AnimatePresence mode="wait">
                {paymentStage === 'confirming' ? (
                  <motion.div
                    key="confirming"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.24 }}
                    className="space-y-4"
                  >
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-400/15 ring-1 ring-amber-300/40">
                      <Loader2 className="h-7 w-7 animate-spin text-amber-300" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-white">Processing your payment</h2>
                      <p className="mt-2 text-sm text-slate-300">We're securely confirming your online order. This usually takes a few seconds.</p>
                    </div>
                    <div className="rounded-xl border border-slate-700/80 bg-slate-800/70 p-3 text-left text-xs text-slate-300">
                      <p className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-emerald-300" /> Payment verification in progress</p>
                      <p className="mt-1 flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-cyan-300" /> Preparing your order confirmation</p>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="confirmed"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.24 }}
                    className="space-y-4"
                  >
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-400/15 ring-1 ring-emerald-300/40">
                      <CheckCircle2 className="h-8 w-8 text-emerald-300" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-white">Your order is confirmed</h2>
                      <p className="mt-2 text-sm text-slate-300">Payment received successfully. We're now preparing your order details.</p>
                    </div>
                    {paymentOrderId && <p className="text-xs text-slate-400">Order Ref: {paymentOrderId.slice(0, 8).toUpperCase()}</p>}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="rounded-3xl border border-slate-200/70 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">My Cart</h1>
              <p className="mt-1 text-sm text-slate-500">Review your items and complete checkout in a few taps.</p>
            </div>
            <div className="flex w-full gap-2 sm:w-auto">
              <Button variant="outline" onClick={() => navigate('/')} className="w-full rounded-xl sm:w-auto">
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back
              </Button>
              <Button variant="outline" onClick={() => navigate('/orders')} className="w-full rounded-xl sm:w-auto">
                <Receipt className="mr-1 h-4 w-4" />
                My Orders
              </Button>
            </div>
          </div>
          <div className="mt-3">
            {cart && (
              <div className="flex items-center gap-2">
                <p className="font-mono text-sm text-slate-600 dark:text-slate-300">#{cart.cart_number || cart.id.slice(0, 6).toUpperCase()}</p>
                <Badge className={statusColor[cart.cart_status] || 'bg-slate-600'}>{cart.cart_status}</Badge>
              </div>
            )}
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
          <Card className="rounded-3xl border-slate-200/80 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <ShoppingBag className="h-5 w-5" />
                Cart Items
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
            {(!cart || !cart.items || cart.items.length === 0) && <p className="text-sm text-slate-500">Your cart is empty</p>}
            {!shopOpen && <p className="rounded-xl bg-amber-100 px-3 py-2 text-sm text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">Shop is currently closed</p>}
            {(cart?.items || []).map((item) => (
              <CartLine
                key={item.item_id}
                item={item}
                disabled={!shopOpen || locked || pendingCounterPayment || pending || itemMutating[item.item_id]}
                onDecrease={() => updateQty(item.item_id, -1)}
                onIncrease={() => updateQty(item.item_id, 1)}
                mutating={Boolean(itemMutating[item.item_id])}
              />
            ))}
            </CardContent>
          </Card>

          <Card className="rounded-3xl border-slate-200/80 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 lg:sticky lg:top-4">
            <CardHeader className="pb-2"><CardTitle className="text-lg">Checkout</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 rounded-2xl bg-slate-50 p-3 text-sm dark:bg-slate-800/60">
                <div className="flex justify-between"><span className="text-slate-500">Actual Price</span><span>₹{priceSummary.actualPrice.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Current Price</span><span>₹{priceSummary.currentPrice.toFixed(2)}</span></div>
                {priceSummary.totalSaved > 0 && <div className="flex justify-between font-medium text-emerald-600"><span>You Saved</span><span>-₹{priceSummary.totalSaved.toFixed(2)}</span></div>}
                <div className="mt-1 flex justify-between border-t pt-2 text-base font-semibold"><span>Payable Total</span><span>₹{priceSummary.payableTotal}</span></div>
                <p className="text-right text-xs text-slate-500">Rounded from ₹{priceSummary.total.toFixed(2)}</p>
              </div>

            {!showPromo && <Button variant="outline" className="w-full rounded-xl" onClick={() => setShowPromo(true)} disabled={!shopOpen || locked || pendingCounterPayment || pending}>Add Promo Code</Button>}
            <Button variant="outline" className="w-full rounded-xl" onClick={() => {
              if (cart?.id) localStorage.setItem('selected_cart_id', cart.id);
              navigate(`/offers${cart?.id ? `?cart_id=${cart.id}` : ''}`);
            }} disabled={!shopOpen || !cart || locked || pendingCounterPayment || pending}>Add Offer</Button>
            {showPromo && (
              <div className="flex gap-2">
                <Input placeholder="Enter promo code" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} disabled={!shopOpen || locked || pendingCounterPayment || pending} />
                <Button variant="outline" onClick={() => applyCode('promo')} disabled={!shopOpen || locked || pendingCounterPayment || pending || !promoCode.trim()}>Apply</Button>
              </div>
            )}
            {cart?.promo_code && (
              <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm dark:border-emerald-900/60 dark:bg-emerald-950/30">
                <span>Promo applied: <strong>{cart.promo_code}</strong></span>
                <Button size="sm" variant="ghost" onClick={() => removeAppliedCode('promo')} disabled={!shopOpen || locked || pendingCounterPayment || pending}>Remove</Button>
              </div>
            )}

            {!showGift && <Button variant="outline" className="w-full rounded-xl" onClick={() => setShowGift(true)} disabled={!shopOpen || locked || pendingCounterPayment || pending}>Add Gift Card</Button>}
            {showGift && (
              <div className="flex gap-2">
                <Input placeholder="Enter gift card" value={giftCardCode} onChange={(e) => setGiftCardCode(e.target.value)} disabled={!shopOpen || locked || pendingCounterPayment || pending} />
                <Button variant="outline" onClick={() => applyCode('gift')} disabled={!shopOpen || locked || pendingCounterPayment || pending || !giftCardCode.trim()}>Apply</Button>
              </div>
            )}
            {cart?.gift_card_code && (
              <div className="flex items-center justify-between rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm dark:border-indigo-900/60 dark:bg-indigo-950/30">
                <span>Gift card applied: <strong>{cart.gift_card_code}</strong></span>
                <Button size="sm" variant="ghost" onClick={() => removeAppliedCode('gift')} disabled={!shopOpen || locked || pendingCounterPayment || pending}>Remove</Button>
              </div>
            )}
            {(cart?.applied_promotions || []).length > 0 && (
              <div className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-300">
                Applied offers: {(cart.applied_promotions || []).map((promotion) => promotion?.name || promotion?.promo_code || 'Offer').join(', ')}
              </div>
            )}

            <div className="grid gap-2 md:grid-cols-2">
              <Button className="rounded-xl h-11" onClick={handleOnlinePayment} disabled={!shopOpen || !cart || locked || pendingCounterPayment || pending || paymentInProgress}>
                {(pending || paymentInProgress) && <Loader2 className="h-4 w-4 animate-spin" />}
                {pending || paymentInProgress ? 'Processing...' : 'Pay Online'}
              </Button>
              <Button variant="secondary" className="h-11 rounded-xl" onClick={handleCounterQR} disabled={!shopOpen || !cart || locked || pendingCounterPayment || pending}>Pay at Counter</Button>
            </div>
            {pendingCounterPayment && (
              <Button variant="destructive" className="w-full rounded-xl" onClick={handleCancelCounterCart} disabled={pending}>
                Cancel This Cart
              </Button>
            )}

            {cart?.qr_code_url && (
              <div className="pt-3 border-t flex flex-col items-center">
                <p className="text-sm font-bold mb-2">Cart Number: {cart.cart_number || '-'}</p>
                <img src={cart.qr_code_url} alt="counter qr" className="w-44 h-44 rounded-xl border" />
              </div>
            )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
